import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Grid,
  TextField,
  Button,
  Typography,
  MenuItem,
  CircularProgress,
  Avatar,
  Tabs,
  Tab,
  Chip,
  IconButton,
  Divider,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import {
  Save,
  ArrowBack,
  Star,
  StarBorder,
  Business,
  Person,
  Phone,
  Email,
  LocationOn,
  Language,
  Settings,
} from '@mui/icons-material';
import { contactAPI } from '../../services/api';
import { toast } from 'react-toastify';

const ContactForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    mobile: '',
    country: 'India',
    state: '',
    city: '',
    street: '',
    zip: '',
    website: '',
    title: '',
    jobPosition: '',
    contactType: 'company',
    isCustomer: false,
    isVendor: false,
    notes: '',
    starred: false,
    tags: [],
  });

  useEffect(() => {
    if (isEdit) {
      fetchContact();
    }
  }, [id]);

  const fetchContact = async () => {
    try {
      setLoading(true);
      const response = await contactAPI.getById(id);
      setFormData(response.data);
    } catch (error) {
      toast.error('Failed to load contact');
      navigate('/contacts');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast.error('Contact name is required');
      return;
    }

    try {
      setSaving(true);

      if (isEdit) {
        await contactAPI.update(id, formData);
        toast.success('Contact updated successfully');
      } else {
        await contactAPI.create(formData);
        toast.success('Contact created successfully');
      }
      navigate('/contacts');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save contact');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStar = () => {
    setFormData({ ...formData, starred: !formData.starred });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ backgroundColor: '#f5f5f5', minHeight: '100vh', ml: -3, mr: -3, mt: -3, mb: -3 }}>
      {/* Top Navigation Bar - Purple */}
      <Box
        sx={{
          backgroundColor: '#875A7B',
          color: 'white',
          px: 2,
          py: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box
              sx={{
                width: 24,
                height: 24,
                backgroundColor: '#4CAF50',
                borderRadius: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '12px',
                fontWeight: 'bold',
              }}
            >
              C
            </Box>
            <Typography variant="body2" sx={{ fontWeight: 500, cursor: 'pointer' }}>
              Contacts
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ cursor: 'pointer' }}>
            Contacts
          </Typography>
          <Typography variant="body2" sx={{ cursor: 'pointer' }}>
            Configuration
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="body2">Tech 4 Logic Private Limited</Typography>
          <Avatar sx={{ width: 28, height: 28, fontSize: '12px' }}>T</Avatar>
        </Box>
      </Box>

      {/* Sub Header */}
      <Box
        sx={{
          backgroundColor: 'white',
          borderBottom: '1px solid #e0e0e0',
          px: 2,
          py: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            variant="contained"
            size="small"
            onClick={() => navigate('/contacts/new')}
            sx={{
              backgroundColor: '#00A09D',
              textTransform: 'none',
              fontWeight: 500,
              px: 2,
              '&:hover': { backgroundColor: '#008784' },
            }}
          >
            New
          </Button>
          <Typography variant="body1" sx={{ color: '#6c757d' }}>
            Contacts
          </Typography>
        </Box>
      </Box>

      {/* Action Bar */}
      <Box
        sx={{
          backgroundColor: 'white',
          borderBottom: '1px solid #e0e0e0',
          px: 2,
          py: 0.5,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <Typography variant="body2" sx={{ color: '#212529' }}>
          {isEdit ? formData.name || 'Edit Contact' : 'New'}
        </Typography>
        <IconButton size="small" onClick={handleToggleStar}>
          {formData.starred ? (
            <Star sx={{ fontSize: 18, color: '#ffc107' }} />
          ) : (
            <StarBorder sx={{ fontSize: 18, color: '#adb5bd' }} />
          )}
        </IconButton>
      </Box>

      {/* Main Content */}
      <Box sx={{ p: 3 }}>
        <Box sx={{ backgroundColor: 'white', borderRadius: 1, p: 3 }}>
          <form onSubmit={handleSubmit}>
            {/* Header with Avatar */}
            <Box sx={{ display: 'flex', gap: 3, mb: 3 }}>
              <Avatar
                sx={{
                  width: 100,
                  height: 100,
                  bgcolor: formData.avatarColor || '#875A7B',
                  fontSize: '36px',
                }}
              >
                {formData.name?.charAt(0)?.toUpperCase() || '?'}
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <TextField
                  fullWidth
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Name"
                  variant="standard"
                  InputProps={{
                    sx: { fontSize: '28px', fontWeight: 400 },
                  }}
                  sx={{ mb: 2 }}
                />
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="contactType"
                        checked={formData.contactType === 'individual'}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            contactType: e.target.checked ? 'individual' : 'company',
                          })
                        }
                        size="small"
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Person sx={{ fontSize: 16 }} />
                        <Typography variant="body2">Individual</Typography>
                      </Box>
                    }
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="contactType"
                        checked={formData.contactType === 'company'}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            contactType: e.target.checked ? 'company' : 'individual',
                          })
                        }
                        size="small"
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Business sx={{ fontSize: 16 }} />
                        <Typography variant="body2">Company</Typography>
                      </Box>
                    }
                  />
                </Box>
              </Box>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Contact Details Grid */}
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                {formData.contactType === 'individual' && (
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Typography sx={{ width: 150, color: '#666', fontSize: '14px' }}>
                      Company
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      placeholder="Company name"
                    />
                  </Box>
                )}
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Typography sx={{ width: 150, color: '#666', fontSize: '14px' }}>
                    <Phone sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
                    Phone
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                  />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Typography sx={{ width: 150, color: '#666', fontSize: '14px' }}>
                    Mobile
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    name="mobile"
                    value={formData.mobile}
                    onChange={handleChange}
                  />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Typography sx={{ width: 150, color: '#666', fontSize: '14px' }}>
                    <Email sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
                    Email
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                  />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Typography sx={{ width: 150, color: '#666', fontSize: '14px' }}>
                    <Language sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
                    Website
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    name="website"
                    value={formData.website}
                    onChange={handleChange}
                    placeholder="e.g. https://www.example.com"
                  />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Typography sx={{ width: 150, color: '#666', fontSize: '14px' }}>
                    Title
                  </Typography>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                  >
                    <MenuItem value="">None</MenuItem>
                    <MenuItem value="Mr.">Mr.</MenuItem>
                    <MenuItem value="Mrs.">Mrs.</MenuItem>
                    <MenuItem value="Ms.">Ms.</MenuItem>
                    <MenuItem value="Dr.">Dr.</MenuItem>
                    <MenuItem value="Prof.">Prof.</MenuItem>
                  </TextField>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Typography sx={{ width: 150, color: '#666', fontSize: '14px' }}>
                    Job Position
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    name="jobPosition"
                    value={formData.jobPosition}
                    onChange={handleChange}
                  />
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                  <Typography sx={{ width: 150, color: '#666', fontSize: '14px', pt: 1 }}>
                    <LocationOn sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
                    Address
                  </Typography>
                  <Box sx={{ flex: 1 }}>
                    <TextField
                      fullWidth
                      size="small"
                      name="street"
                      value={formData.street}
                      onChange={handleChange}
                      placeholder="Street..."
                      sx={{ mb: 1 }}
                    />
                    <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                      <TextField
                        size="small"
                        name="city"
                        value={formData.city}
                        onChange={handleChange}
                        placeholder="City"
                        sx={{ flex: 1 }}
                      />
                      <TextField
                        size="small"
                        name="state"
                        value={formData.state}
                        onChange={handleChange}
                        placeholder="State"
                        sx={{ flex: 1 }}
                      />
                      <TextField
                        size="small"
                        name="zip"
                        value={formData.zip}
                        onChange={handleChange}
                        placeholder="ZIP"
                        sx={{ width: 100 }}
                      />
                    </Box>
                    <TextField
                      fullWidth
                      size="small"
                      name="country"
                      value={formData.country}
                      onChange={handleChange}
                      placeholder="Country"
                    />
                  </Box>
                </Box>
              </Grid>
            </Grid>

            {/* Tabs */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mt: 3 }}>
              <Tabs
                value={activeTab}
                onChange={(e, newValue) => setActiveTab(newValue)}
                sx={{
                  '& .MuiTab-root': {
                    textTransform: 'none',
                    minWidth: 'auto',
                    px: 2,
                    color: '#666',
                  },
                }}
              >
                <Tab label="Contacts & Addresses" />
                <Tab label="Sales & Purchase" />
                <Tab label="Internal Notes" />
              </Tabs>
            </Box>

            {activeTab === 0 && (
              <Box sx={{ py: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Child contacts and addresses will appear here.
                </Typography>
              </Box>
            )}

            {activeTab === 1 && (
              <Box sx={{ py: 2 }}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          name="isCustomer"
                          checked={formData.isCustomer}
                          onChange={handleChange}
                          size="small"
                        />
                      }
                      label="Is a Customer"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          name="isVendor"
                          checked={formData.isVendor}
                          onChange={handleChange}
                          size="small"
                        />
                      }
                      label="Is a Vendor"
                    />
                  </Grid>
                </Grid>
              </Box>
            )}

            {activeTab === 2 && (
              <Box sx={{ py: 2 }}>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  placeholder="Internal notes..."
                />
              </Box>
            )}

            {/* Actions */}
            <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
              <Button variant="outlined" onClick={() => navigate('/contacts')}>
                Discard
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={saving}
                sx={{
                  backgroundColor: '#875A7B',
                  '&:hover': { backgroundColor: '#714B68' },
                }}
              >
                {saving ? <CircularProgress size={20} color="inherit" /> : 'Save'}
              </Button>
            </Box>
          </form>
        </Box>
      </Box>
    </Box>
  );
};

export default ContactForm;
